// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Loads Google reCAPTCHA v3 on demand and mints a token per action. The backend checks the
// action name and hostname, so a token minted for one step can't be replayed for another.
// ─────────────────────────────────────────────────────────────────────────────
import { ApiError } from "./api";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let scriptPromise: Promise<Grecaptcha> | null = null;

const READY_TIMEOUT_MS = 10_000;

function loadScript(siteKey: string): Promise<Grecaptcha> {
  if (scriptPromise) return scriptPromise;
  const attempt = new Promise<Grecaptcha>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("reCAPTCHA timed out")), READY_TIMEOUT_MS);
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) return reject(new Error("reCAPTCHA did not initialise"));
      grecaptcha.ready(() => {
        clearTimeout(timer);
        resolve(grecaptcha);
      });
    };
    script.onerror = () => reject(new Error("reCAPTCHA failed to load"));
    document.head.appendChild(script);
  });
  // Any failure clears the cache, so one blip doesn't disable login for the whole tab.
  scriptPromise = attempt.catch((error: unknown) => {
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}

/**
 * A reCAPTCHA v3 token for `action`. The backend checks the action too, so a token minted
 * for one action can't be replayed against another.
 *
 * With no site key configured: dev builds get a placeholder (the backend accepts anything
 * while it uses Google's documented test secret); production builds refuse, with an honest
 * error, rather than send a request that is certain to be rejected.
 */
export async function getRecaptchaToken(action: string): Promise<string> {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    if (import.meta.env.DEV) return "dev-recaptcha-token";
    throw new ApiError(0, "RECAPTCHA_NOT_CONFIGURED", "Login isn't available right now.");
  }
  try {
    const grecaptcha = await loadScript(siteKey);
    return await grecaptcha.execute(siteKey, { action });
  } catch {
    throw new ApiError(
      0,
      "RECAPTCHA_LOAD_FAILED",
      "We couldn't run the security check. Check your connection and try again.",
    );
  }
}
